import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import {
  Loader2, RefreshCw, Server, Database, HardDrive, MessageCircle, Sparkles, Bell, CreditCard,
  CheckCircle2, AlertTriangle, XCircle, MinusCircle, X, Rocket, Tag, ShieldAlert,
} from 'lucide-react';

const SERVICES = [
  { key: 'backend', label: 'Backend', icon: Server },
  { key: 'database', label: 'Database', icon: Database },
  { key: 'storage', label: 'Storage', icon: HardDrive },
  { key: 'whatsapp', label: 'WhatsApp API', icon: MessageCircle },
  { key: 'ai_llm', label: 'AI / LLM', icon: Sparkles },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'payments', label: 'Payment Service', icon: CreditCard },
];

const STATUS = {
  operational: { label: 'Operational', dot: 'bg-emerald-500', text: 'text-emerald-600', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  configured: { label: 'Configured', dot: 'bg-indigo-500', text: 'text-indigo-600', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: CheckCircle2 },
  warning: { label: 'Warning', dot: 'bg-amber-500', text: 'text-amber-600', chip: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
  down: { label: 'Down', dot: 'bg-rose-500', text: 'text-rose-600', chip: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle },
  not_configured: { label: 'Not Configured', dot: 'bg-neutral-400', text: 'text-neutral-500', chip: 'bg-neutral-100 text-neutral-600 border-neutral-200', icon: MinusCircle },
  unable: { label: 'Unable to Check', dot: 'bg-neutral-400', text: 'text-neutral-500', chip: 'bg-neutral-100 text-neutral-600 border-neutral-200', icon: MinusCircle },
};
const st = (s) => STATUS[s] || STATUS.unable;

function fmtWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const SUMMARY_BANNER = {
  operational: { cls: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700', icon: CheckCircle2 },
  warning: { cls: 'bg-amber-50 border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', icon: AlertTriangle },
  critical: { cls: 'bg-rose-50 border-rose-200', dot: 'bg-rose-500', text: 'text-rose-700', icon: ShieldAlert },
};

export default function AdminPlatformHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [lastOk, setLastOk] = useState(null);
  const [detail, setDetail] = useState(null);
  const timer = useRef(null);

  const load = useCallback((manual = false) => {
    if (manual) setRefreshing(true);
    api.get('/admin/platform-health')
      .then(({ data }) => { setData(data); setError(false); setLastOk(data.generated_at); })
      .catch(() => setError(true))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(() => load(), 60000);
    return () => clearInterval(timer.current);
  }, [load]);

  if (loading) {
    return <div className="flex justify-center p-20" data-testid="ph-loading"><Loader2 className="w-7 h-7 animate-spin text-fab-primary" /></div>;
  }

  if (error && !data) {
    return (
      <div data-testid="admin-platform-health">
        <Header onRefresh={() => load(true)} refreshing={refreshing} />
        <div className="mt-6 bg-white border border-rose-200 rounded-xl p-10 text-center" data-testid="ph-error">
          <ShieldAlert className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <div className="font-semibold text-fab-text text-[15px]">Unable to load platform health.</div>
          <div className="text-[13px] text-fab-text-muted mt-1">Last successful check: {lastOk ? fmtWhen(lastOk) : 'unknown'}</div>
          <button onClick={() => load(true)} data-testid="ph-retry" className="mt-4 px-5 py-2.5 text-sm font-semibold rounded-lg bg-fab-primary text-white hover:bg-fab-primary-dark">Retry</button>
        </div>
      </div>
    );
  }

  const summary = data.summary || { state: 'operational', label: 'All Systems Operational', attention: [] };
  const sb = SUMMARY_BANNER[summary.state] || SUMMARY_BANNER.operational;
  const dep = data.deployment || {};

  return (
    <div data-testid="admin-platform-health">
      <Header onRefresh={() => load(true)} refreshing={refreshing} />

      {/* Platform Status summary */}
      <div className={`mt-4 border rounded-xl px-4 py-3.5 flex items-center gap-3 ${sb.cls}`} data-testid="ph-summary">
        <span className={`w-2.5 h-2.5 rounded-full ${sb.dot} animate-pulse`} />
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-fab-text-muted">Platform Status</div>
          <div className={`text-[15px] font-bold ${sb.text}`} data-testid="ph-summary-label">{summary.label}</div>
        </div>
        {summary.attention && summary.attention.length > 0 && (
          <div className="text-right text-[12px] text-fab-text-secondary hidden sm:block">
            {summary.attention.slice(0, 2).map((a) => (
              <div key={a.service}>{SERVICES.find((s) => s.key === a.service)?.label || a.service} — <span className="capitalize font-medium">{st(a.status).label}</span></div>
            ))}
          </div>
        )}
      </div>

      {/* System Services */}
      <h3 className="mt-6 mb-3 font-display text-lg font-bold text-fab-text">System Services</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        {SERVICES.map(({ key, label, icon: Icon }) => {
          const s = data.services?.[key] || { status: 'unable', message: 'No data', last_checked: null };
          const cfg = st(s.status);
          return (
            <div key={key} className="bg-white border border-fab-primary-lighter rounded-xl p-4 flex flex-col" data-testid={`ph-card-${key}`}>
              <div className="flex items-center justify-between">
                <span className="w-9 h-9 rounded-lg bg-fab-primary-lighter grid place-items-center text-fab-primary"><Icon className="w-[18px] h-[18px]" /></span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-semibold ${cfg.chip}`} data-testid={`ph-status-${key}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
                </span>
              </div>
              <div className="mt-3 font-semibold text-fab-text text-[14px]">{label}</div>
              <div className="text-[12px] text-fab-text-secondary mt-0.5 min-h-[32px] leading-snug">{s.message}</div>
              <div className="mt-3 pt-2.5 border-t border-fab-primary-lighter/70 flex items-center justify-between">
                <div className="text-[10.5px] text-fab-text-muted">
                  <div className="uppercase tracking-wider">Last checked</div>
                  <div className="text-fab-text-secondary">{fmtWhen(s.last_checked)}</div>
                </div>
                <button onClick={() => setDetail({ key, label, ...s })} data-testid={`ph-details-${key}`}
                  className="text-[11.5px] font-semibold text-fab-primary hover:underline">View Details</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Deployment Information */}
      <h3 className="mt-8 mb-3 font-display text-lg font-bold text-fab-text">Deployment Information</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <div className="bg-white border border-fab-primary-lighter rounded-xl p-5" data-testid="ph-last-deploy">
          <div className="flex items-center gap-2 text-fab-primary"><Rocket className="w-5 h-5" /><span className="text-[11px] uppercase tracking-wider font-semibold text-fab-text-muted">{dep.last_deployment_label || 'Last Deployment'}</span></div>
          <div className="mt-2 text-xl font-bold text-fab-text">{fmtWhen(dep.last_deployment)}</div>
          <div className="text-[12.5px] text-fab-text-secondary mt-1">{dep.deployed_by_label || 'Deployed by'} <span className="font-medium text-fab-text">{dep.deployed_by || 'Not available'}</span></div>
        </div>
        <div className="bg-white border border-fab-primary-lighter rounded-xl p-5" data-testid="ph-version">
          <div className="flex items-center gap-2 text-fab-primary"><Tag className="w-5 h-5" /><span className="text-[11px] uppercase tracking-wider font-semibold text-fab-text-muted">Current Application Version</span></div>
          <div className="mt-2 text-xl font-bold text-fab-text">{dep.version && dep.version !== 'Not available' ? `v${dep.version}` : 'Not available'}</div>
          <div className="text-[12.5px] text-fab-text-secondary mt-1">Build <span className="font-mono text-fab-text">{dep.build || 'Not available'}</span></div>
        </div>
      </div>

      {/* Footer strip */}
      <div className="mt-6 text-center text-[12px] text-fab-text-muted" data-testid="ph-footer">
        All system health checks are performed automatically every {data.auto_refresh_seconds || 60} seconds. Page auto-refresh: {data.auto_refresh_seconds || 60}s
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="ph-detail-drawer">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
            <div className="p-5 border-b border-fab-primary-lighter flex items-start justify-between sticky top-0 bg-white">
              <div>
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-semibold ${st(detail.status).chip}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${st(detail.status).dot}`} />{st(detail.status).label}
                </div>
                <h3 className="text-lg font-bold text-fab-text mt-2">{detail.label}</h3>
              </div>
              <button onClick={() => setDetail(null)} data-testid="ph-detail-close" className="text-fab-text-muted hover:text-fab-text"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 text-[13px]">
              <dl className="grid grid-cols-3 gap-y-2.5">
                <dt className="text-fab-text-muted">Service</dt><dd className="col-span-2 font-medium text-fab-text">{detail.label}</dd>
                <dt className="text-fab-text-muted">Status</dt><dd className="col-span-2 font-medium capitalize text-fab-text">{st(detail.status).label}</dd>
                <dt className="text-fab-text-muted">Last Checked</dt><dd className="col-span-2 text-fab-text">{fmtWhen(detail.last_checked)}</dd>
              </dl>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Health Message</div>
                <div className="text-fab-text">{detail.message}</div>
              </div>
              {detail.detail && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Diagnostic Details</div>
                  <div className="bg-fab-base/60 border border-fab-primary-lighter rounded-lg p-2.5 font-mono text-[11.5px] text-fab-text-secondary break-all">{detail.detail}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Header({ onRefresh, refreshing }) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-fab-primary-lighter grid place-items-center text-fab-primary shrink-0"><Server className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-2xl font-bold text-fab-text" data-testid="ph-title">Platform Health</h2>
          <p className="text-sm text-fab-text-secondary mt-0.5">Real-time status of all critical systems and services.</p>
        </div>
      </div>
      <button onClick={onRefresh} disabled={refreshing} data-testid="ph-refresh"
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary hover:text-fab-primary transition disabled:opacity-60">
        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
      </button>
    </div>
  );
}
